import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCreateEntityType } from "../../lib/hooks/use-kuvio";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type { EntityTypeDefinition } from "../../lib/types";

interface CreateTableParams {
  definition: Omit<EntityTypeDefinition, "_version">;
  /**
   * The agent this table belongs to. Sprint 14c: the table is OWNED by this
   * agent (owner_agent_id) but lives at WORKSPACE ROOT — not nested inside it.
   * Pass the workspace root id to create an unowned, workspace-scoped table.
   */
  parentAgentId: string;
}

export function useCreateTable() {
  const qc = useQueryClient();
  const createEntityType = useCreateEntityType();

  return useMutation({
    mutationFn: async ({ definition, parentAgentId }: CreateTableParams) => {
      // Sprint 14c (S14C.22): create the type with owner_agent_id set to the
      // agent and let the table node default to workspace root. The previous
      // behaviour MOVED the node under the agent (nesting), which is exactly the
      // symptom this sprint fixes — tables are workspace-scoped data; the agent
      // owns them via metadata, not tree containment.
      const created = await createEntityType.mutateAsync({
        ...definition,
        owner_agent_id: parentAgentId,
        caller_node_id: parentAgentId,
      } as Omit<EntityTypeDefinition, "_version">);

      let tableNodeId = created._table_node_id;
      if (!tableNodeId) {
        const nodes = await mcpClient.listTree({ type: "table" });
        const tableNode = nodes.find((n) => n.entity_type_slug === definition.slug);
        tableNodeId = tableNode?.id;
      }

      return { entityType: created, tableNodeId };
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.listChildren(vars.parentAgentId) });
      qc.invalidateQueries({ queryKey: queryKeys.entityTypes() });
      qc.invalidateQueries({ queryKey: queryKeys.libraryTree() });
      qc.invalidateQueries({ queryKey: queryKeys.workspace() });
    },
  });
}
